import React from 'react';
import styles from './ProductSpecs.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faInfoCircle,
  faCog,
  faRuler,
  faWeight,
  faTag,
  faCertificate,
  faShieldAlt
} from '@fortawesome/free-solid-svg-icons';

const ProductSpecs = ({ product }) => {
  // Обработка спецификаций - они могут приходить в разных форматах
  const getSpecifications = () => {
    if (!product) return [];
    
    // Если спецификации уже в нужном формате
    if (product.specifications && Array.isArray(product.specifications)) {
      return product.specifications;
    }
    
    // Если спецификации в виде объекта - преобразуем в массив
    if (product.specifications && typeof product.specifications === 'object') {
      return Object.entries(product.specifications).map(([key, value]) => ({
        category: 'Общие характеристики',
        label: key,
        value: value,
        icon: faInfoCircle
      }));
    }
    
    // Создаем базовые спецификации из доступных полей продукта
    const specs = [];
    
    if (product.brand) {
      specs.push({
        category: 'Основная информация',
        label: 'Бренд',
        value: product.brand,
        icon: faTag
      });
    }
    
    if (product.model) {
      specs.push({
        category: 'Основная информация',
        label: 'Модель',
        value: product.model,
        icon: faInfoCircle
      });
    }
    
    if (product.category) {
      specs.push({
        category: 'Основная информация',
        label: 'Категория',
        value: product.category,
        icon: faTag
      });
    }
    
    if (product.weight) {
      specs.push({
        category: 'Физические характеристики',
        label: 'Вес',
        value: `${product.weight} кг`,
        icon: faWeight
      });
    }
    
    if (product.dimensions) {
      specs.push({
        category: 'Физические характеристики',
        label: 'Размеры',
        value: product.dimensions,
        icon: faRuler
      });
    }
    
    if (product.warranty) {
      specs.push({
        category: 'Гарантии и сертификация',
        label: 'Гарантия',
        value: product.warranty,
        icon: faShieldAlt
      });
    }
    
    if (product.material) {
      specs.push({
        category: 'Технические характеристики',
        label: 'Материал',
        value: product.material,
        icon: faCog
      });
    }
    
    if (product.color) {
      specs.push({
        category: 'Внешний вид',
        label: 'Цвет',
        value: product.color,
        icon: faInfoCircle
      });
    }
    
    if (product.sku) {
      specs.push({
        category: 'Служебная информация',
        label: 'Артикул',
        value: product.sku,
        icon: faInfoCircle
      });
    }
    
    return specs;
  };
  
  const specifications = getSpecifications();
  
  // Группируем спецификации по категориям
  const groupedSpecs = specifications.reduce((acc, spec) => {
    const category = spec.category || 'Общие характеристики';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(spec);
    return acc;
  }, {});
  
  // Если нет спецификаций
  if (specifications.length === 0) {
    return (
      <div className={styles.specsContainer}>
        <div className={styles.noSpecs}>
          <FontAwesomeIcon icon={faInfoCircle} />
          <h3>Технические характеристики</h3>
          <p>Подробные технические характеристики для данного товара будут добавлены позже.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={styles.specsContainer}>
      <div className={styles.specsHeader}>
        <h2>Технические характеристики</h2>
        <p>Подробная информация о товаре и его характеристиках</p>
      </div>
      
      <div className={styles.specsGrid}>
        {Object.entries(groupedSpecs).map(([category, specs]) => (
          <div key={category} className={styles.specsCategory}>
            <h3 className={styles.categoryTitle}>
              <FontAwesomeIcon icon={getCategoryIcon(category)} />
              {category}
            </h3>
            
            <div className={styles.specsTable}>
              {specs.map((spec, index) => (
                <div key={index} className={styles.specRow}>
                  <div className={styles.specLabel}>
                    {spec.icon && (
                      <FontAwesomeIcon icon={spec.icon} className={styles.specIcon} />
                    )}
                    {spec.label}
                  </div>
                  <div className={styles.specValue}>
                    {Array.isArray(spec.value) ? spec.value.join(', ') : spec.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      {/* Дополнительная информация */}
      {(product.description || product.features) && (
        <div className={styles.additionalInfo}>
          <h3>Дополнительная информация</h3>
          
          {product.features && Array.isArray(product.features) && (
            <div className={styles.features}>
              <h4>Особенности:</h4>
              <ul>
                {product.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>
          )}
          
          {product.included && Array.isArray(product.included) && (
            <div className={styles.included}>
              <h4>В комплекте:</h4>
              <ul>
                {product.included.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          
          {product.notes && (
            <div className={styles.notes}>
              <h4>Примечания:</h4>
              <p>{product.notes}</p>
            </div>
          )}
        </div>
      )}
      
      {/* Сертификаты и документы */}
      {(product.certificates || product.documents) && (
        <div className={styles.certificates}>
          <h3>
            <FontAwesomeIcon icon={faCertificate} />
            Сертификаты и документы
          </h3>
          
          <div className={styles.documentsGrid}>
            {product.certificates && product.certificates.map((cert, index) => (
              <div key={index} className={styles.document}>
                <FontAwesomeIcon icon={faCertificate} />
                <span>{cert}</span>
              </div>
            ))}
            
            {product.documents && product.documents.map((doc, index) => (
              <div key={index} className={styles.document}>
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>{doc}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Функция для получения иконки категории
const getCategoryIcon = (category) => {
  const icons = {
    'Основная информация': faInfoCircle,
    'Технические характеристики': faCog,
    'Физические характеристики': faRuler,
    'Гарантии и сертификация': faShieldAlt,
    'Внешний вид': faTag,
    'Служебная информация': faInfoCircle,
    'Общие характеристики': faInfoCircle
  };
  
  return icons[category] || faInfoCircle;
};

export default ProductSpecs;